import React from 'react';
import {
  Heart,
  ShieldCheck,
  Lock,
  Star,
  CreditCard,
  MessageCircle,
  CalendarCheck,
  LifeBuoy,
  Sparkles,
  CheckCircle2,
  Users,
  Compass
} from 'lucide-react';

interface AboutSectionProps {
  onOpenSafety?: () => void;
  onExploreCompanions?: () => void;
}

export const AboutSection: React.FC<AboutSectionProps> = ({
  onOpenSafety,
  onExploreCompanions
}) => {
  const whyItems = [
    {
      icon: Heart,
      title: 'Companionship for Every Occasion',
      desc: 'Weddings, social dinners, casual cafe dates, travel, photoshoots & galas.',
      color: 'from-pink-500 to-rose-600',
      bg: 'bg-pink-50 text-pink-600 border-pink-100'
    },
    {
      icon: ShieldCheck,
      title: 'Verified Profiles',
      desc: 'Government ID checked & background-verified real companions.',
      color: 'from-purple-500 to-indigo-600',
      bg: 'bg-purple-50 text-purple-600 border-purple-100'
    },
    {
      icon: Lock,
      title: 'Privacy & Safety First',
      desc: 'End-to-end confidential bookings with zero personal data leakage.',
      color: 'from-emerald-500 to-teal-600',
      bg: 'bg-emerald-50 text-emerald-600 border-emerald-100'
    },
    {
      icon: Star,
      title: 'Ratings & Reviews',
      desc: 'Transparent feedback from verified users for every completed meet.',
      color: 'from-amber-500 to-yellow-600',
      bg: 'bg-amber-50 text-amber-600 border-amber-100'
    },
    {
      icon: CreditCard,
      title: 'Secure Online Payments',
      desc: '100% Escrow protected UPI, Cards, and NetBanking guarantees.',
      color: 'from-blue-500 to-cyan-600',
      bg: 'bg-blue-50 text-blue-600 border-blue-100'
    },
    {
      icon: MessageCircle,
      title: 'Safe In-App Communication',
      desc: 'Communicate smoothly through verified channels before you meet.',
      color: 'from-rose-500 to-pink-600',
      bg: 'bg-rose-50 text-rose-600 border-rose-100'
    },
    {
      icon: CalendarCheck,
      title: 'Easy Booking & Availability',
      desc: 'Instant hourly slots and flexible dates matching your schedule.',
      color: 'from-violet-500 to-purple-600',
      bg: 'bg-violet-50 text-violet-600 border-violet-100'
    },
    {
      icon: LifeBuoy,
      title: 'Dedicated Customer Support',
      desc: '24/7 dedicated client assistance desk ready to help at every step.',
      color: 'from-indigo-500 to-blue-600',
      bg: 'bg-indigo-50 text-indigo-600 border-indigo-100'
    }
  ];

  return (
    <section id="about-section" className="py-16 sm:py-24 bg-gradient-to-b from-white via-rose-50/30 to-white relative overflow-hidden border-t border-pink-100/60">
      
      {/* Subtle background ambient blur */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] max-w-full h-[350px] bg-gradient-to-tr from-pink-200/30 via-purple-200/20 to-transparent rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Heading & Badge */}
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-100/80 border border-pink-200/80 text-pink-700 text-xs font-bold tracking-wide shadow-xs">
            <Heart className="w-3.5 h-3.5 fill-pink-600 text-pink-600 animate-pulse" />
            <span>ABOUT US</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            About <span className="text-slate-900">PairUp</span><span className="text-pink-600">Today</span> <span className="text-rose-500 inline-block">❤️</span>
          </h2>

          <p className="text-base sm:text-lg text-slate-600 font-medium leading-relaxed pt-1">
            PairUpToday is a modern companionship platform designed to help people find the right companion for meaningful moments and memorable occasions.
          </p>
        </div>

        {/* Narrative Intro Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-pink-100 shadow-sm hover:shadow-md transition-all space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-pink-500 to-rose-600 flex items-center justify-center text-white shadow-md shadow-pink-500/20">
              <Compass className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              Find Companionship for Every Moment
            </h3>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              Whether you are looking for someone to accompany you to a date, social event, party, travel experience, photoshoot, wedding, or simply want good company, PairUpToday makes discovering and connecting with companions simple, convenient, and secure.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-purple-100 shadow-sm hover:shadow-md transition-all space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-purple-500 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-purple-500/20">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              Built on Choice, Privacy & Trust
            </h3>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              Our platform is built around choice, privacy, safety, and trust. We aim to provide a comfortable environment where users can explore verified profiles, view ratings and reviews, check availability, communicate through the platform, and make bookings with confidence.
            </p>
          </div>

        </div>

        {/* Our Mission Banner */}
        <div className="bg-gradient-to-r from-slate-900 via-purple-950 to-slate-900 rounded-3xl p-8 sm:p-12 text-white shadow-xl relative overflow-hidden mb-16 border border-purple-900/60">
          <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-pink-500/10 rounded-full blur-2xl pointer-events-none" />
          
          <div className="max-w-3xl space-y-4 relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-pink-500/20 border border-pink-500/30 text-pink-300 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-pink-400" />
              <span>Our Mission</span>
            </div>

            <h3 className="text-xl sm:text-2xl font-bold text-pink-200">
              Our mission is simple:
            </h3>

            <blockquote className="text-2xl sm:text-3xl font-extrabold text-white leading-snug tracking-tight border-l-4 border-pink-500 pl-4 sm:pl-6 my-4 italic">
              “To make finding the right companion easier, safer, and more comfortable for everyone.”
            </blockquote>

            <p className="text-slate-300 text-sm sm:text-base leading-relaxed pt-2">
              We believe that nobody should have to experience important moments alone. PairUpToday brings people together so they can meet, connect, share experiences, and create memorable moments.
            </p>
          </div>
        </div>

        {/* Why PairUpToday Section Grid */}
        <div className="mb-16 space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              Why PairUpToday?
            </h3>
            <p className="text-sm text-slate-500">
              Experience the highest standard of companionship services in India
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {whyItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200/80 hover:border-pink-300 shadow-xs hover:shadow-lg transition-all duration-300 flex flex-col items-start group"
                >
                  <div className={`w-11 h-11 rounded-xl ${item.bg} border flex items-center justify-center mb-3.5 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="text-sm sm:text-base font-bold text-slate-900 group-hover:text-pink-600 transition-colors mb-1.5">
                    {item.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Our Promise & Community Guidelines Banner */}
        <div className="bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 rounded-3xl p-6 sm:p-10 border border-pink-200/80 shadow-sm relative overflow-hidden">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-white border border-pink-200 text-pink-700 text-xs font-extrabold">
                <CheckCircle2 className="w-3.5 h-3.5 text-pink-600" />
                <span>Our Promise</span>
              </div>
              <h4 className="text-xl sm:text-2xl font-extrabold text-slate-900">
                A Respectful, Safe & Transparent Platform
              </h4>
              <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                At PairUpToday, we are committed to maintaining a respectful, safe, transparent, and inclusive platform. We follow strict community guidelines and do not allow harassment, exploitation, fraudulent profiles, or services that violate our platform rules.
              </p>
              <div className="pt-1 flex items-center gap-2 text-pink-700 font-extrabold text-sm sm:text-base">
                <span>PairUpToday — Find Your Perfect Companion.</span>
                <Heart className="w-4 h-4 fill-pink-600 text-pink-600 inline" />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
              <button
                type="button"
                onClick={onOpenSafety}
                className="px-5 py-3 rounded-xl bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 text-xs sm:text-sm font-bold shadow-xs hover:shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>Safety Guidelines</span>
              </button>
              <button
                type="button"
                onClick={onExploreCompanions}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white text-xs sm:text-sm font-bold shadow-md shadow-pink-500/25 hover:shadow-pink-500/40 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Heart className="w-4 h-4 fill-white" />
                <span>Browse Verified Companions</span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
